import { useEffect, useRef, useState, type CSSProperties, type ElementType, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
  as?: ElementType;
  className?: string;
  /** 1–3: a small stagger for items revealed together. */
  delay?: 1 | 2 | 3;
  id?: string;
  /**
   * Skip the wrapper's own fade: only toggle the in-view classes, for design
   * classes that animate their children instead (.stagger, .reveal-img,
   * .statement).
   */
  bare?: boolean;
  style?: CSSProperties;
  'aria-labelledby'?: string;
}

/**
 * Eases its content up into place the first time it scrolls into view.
 * Motion is CSS-only (.reveal / .reveal.in, plus the design's .is-in), so
 * prefers-reduced-motion turns it off entirely, and without
 * IntersectionObserver the content just shows.
 */
export function Reveal({ children, as: Tag = 'div', className = '', delay, id, bare = false, style, ...rest }: Props) {
  const ref = useRef<HTMLElement>(null);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === 'undefined') {
      setShown(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setShown(true);
          io.disconnect();
        }
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.08 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <Tag
      ref={ref}
      id={id}
      style={style}
      aria-labelledby={rest['aria-labelledby']}
      className={[bare ? '' : 'reveal', shown ? 'in is-in' : '', className].filter(Boolean).join(' ')}
      data-delay={delay}
    >
      {children}
    </Tag>
  );
}
